import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    HistoryEntry,
    Case,
    CaseCycle,
    CaseTeamMember,
    SystemEntity,
    WorkItemCategorySummary,
    HeatMap,
} from '@wdx/clmi/clmi-swagger-gen';
import { CaseWork } from '../../models/case-work.model';
import { ConfigService } from '../../services/config.service';
import { Observable } from 'rxjs';
import { PaginatedApiResponse } from '@wdx/shared/utils';
import { ApiPaginationService } from '@wdx/shared/infrastructure/api-service';

@Injectable()
export class CasesService {
    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private apiPaginationService: ApiPaginationService,
    ) {}

    getCases(entityType: SystemEntity, entityId: string): Observable<Case[]> {
        return this.http.get<Case[]>(
            `${
                this.config.getConfiguration().API_BASE
            }/${entityType.toLowerCase()}/${entityId}/case?activeOnly=false`,
        );
    }

    getCase(caseId: string): Observable<Case> {
        return this.http.get<Case>(
            `${this.config.getConfiguration().API_BASE}/case/${caseId}`,
        );
    }

    getCaseWork(caseId: string): Observable<CaseWork[]> {
        return this.http.get<CaseWork[]>(
            `${this.config.getConfiguration().API_BASE}/case/${caseId}/work`,
        );
    }

    getCaseHistory(
        caseId: string,
    ): Observable<PaginatedApiResponse<HistoryEntry>> {
        return this.http.get<PaginatedApiResponse<HistoryEntry>>(
            `${this.config.getConfiguration().API_BASE}/case/${caseId}/history`,
            {
                headers: this.apiPaginationService.mapPaginationOptions({
                    pageSize: 200,
                }),
            },
        );
    }

    getCasesForClient(clientId: string): Observable<Case[]> {
        return this.http.get<Case[]>(
            `${this.config.getConfiguration().API_BASE}/client/${clientId}/case`,
        );
    }

    getFavourites(): Observable<Case[]> {
        return this.http.get<Case[]>(
            `${this.config.getConfiguration().API_BASE}/case/favourite`,
        );
    }

    // Team members

    assignTeamMemberToCase(
        caseId: string,
        partyId: string,
    ): Observable<CaseTeamMember> {
        return this.http.patch<CaseTeamMember>(
            `${
                this.config.getConfiguration().API_BASE
            }/case/${caseId}/member/${partyId}`,
            {},
        );
    }

    deleteTeamMemberFromCase(
        caseId: string,
        partyId: string,
    ): Observable<CaseTeamMember> {
        return this.http.delete<CaseTeamMember>(
            `${
                this.config.getConfiguration().API_BASE
            }/case/${caseId}/member/${partyId}`,
            {},
        );
    }

    getAllMembersForCase(caseId: string): Observable<CaseTeamMember[]> {
        return this.http.get<CaseTeamMember[]>(
            `${this.config.getConfiguration().API_BASE}/case/${caseId}/member`,
        );
    }

    completeCase(caseId: string): Observable<Case> {
        return this.http.patch<Case>(
            `${
                this.config.getConfiguration().API_BASE
            }/case/${caseId}/complete`,
            {},
        );
    }

    cancelCase(caseId: string): Observable<Case> {
        return this.http.patch<Case>(
            `${this.config.getConfiguration().API_BASE}/case/${caseId}/cancel`,
            {},
        );
    }

    getCategoriesForCase(
        caseId: string,
    ): Observable<WorkItemCategorySummary[]> {
        return this.http.get<WorkItemCategorySummary[]>(
            `${
                this.config.getConfiguration().API_BASE
            }/case/${caseId}/categories`,
        );
    }

    getReviewsForId(id: string): Observable<CaseCycle[]> {
        return this.http.get<CaseCycle[]>(
            `${this.config.getConfiguration().API_BASE}/case/${id}/review`,
        );
    }

    getKycHeatmap(id: string): Observable<HeatMap> {
        return this.http.get<HeatMap>(
            `${this.config.getConfiguration().API_BASE}/case/${id}/heatmap/kyc`,
        );
    }

    getCaseHeatmap(id: string): Observable<HeatMap> {
        return this.http.get<HeatMap>(
            `${this.config.getConfiguration().API_BASE}/case/${id}/heatmap`,
        );
    }
}
